import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, X, Sparkles, CheckSquare, ChevronLeft, ChevronRight, Target, Clock3, CheckCircle2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { UserSearchInput } from '@/components/tasks/UserSearchInput';
import { taskService } from '@/lib/api/task.service';
import { eventService } from '@/lib/api/event.service';
import { userService } from '@/lib/api/user.service';
import { blockerService } from '@/lib/api/blocker.service';
import { activityService } from '@/lib/api/activity.service';
import { goalService } from '@/lib/api/goal.service';
import { TaskTemplatePicker } from '@/components/tasks/TaskTemplatePicker';
import { TaskGrid } from '@/components/tasks/TaskGrid';
import type { Task, CreateTaskDto, TaskStatus } from '@/types/task.types';
import type { UserContact } from '@/types/user-contact.types';
import type { Blocker, BlockerMode } from '@/types/blocker.types';
import type { Activity } from '@/types/activity.types';
import type { GoalInstanceWithDetails } from '@/types/goal.types';
import type { Event } from '@/types/event.types';

type TaskViewMode = 'list' | 'goals' | 'calendar';
type BlockerTargetEntityType = 'task' | 'activity' | 'goal' | 'event';
type TaskBlockedByEntityType = 'task' | 'goal' | 'event';
type TaskBlockingEntityType = 'task' | 'goal' | 'event';
type BlockerEditorMode = 'blockedBy' | 'blocking';

interface BlockedByInlineFormState {
  entityType: TaskBlockedByEntityType;
  entityId: string;
  eventStatus: string;
  mode: BlockerMode;
}

interface BlockingInlineFormState {
  entityType: TaskBlockingEntityType;
  entityId: string;
  eventStatus: string;
  mode: BlockerMode;
}

interface EntityOption {
  id: number;
  label: string;
}

interface PendingCreateBlocker {
  id: string;
  entityType: TaskBlockedByEntityType | TaskBlockingEntityType;
  entityId: number;
  eventStatus?: string;
  mode: BlockerMode;
}

const TASK_VIEW_MODE_STORAGE_KEY = 'om.tasks.viewMode';
const CALENDAR_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EVENT_STATUS_OPTIONS = ['draft', 'planning', 'planned', 'active', 'completed', 'cancelled'] as const;

const toDateKey = (value: Date | string): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateKey = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const TaskManagement: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [usernames, setUsernames] = useState<Record<number, string>>({});
  const [selectedUser, setSelectedUser] = useState<UserContact | null>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const [deepLinkedTaskId, setDeepLinkedTaskId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTaskDto>({
    title: '',
    description: '',
    assigned_to: undefined,
    due_date: '',
    status: 'pending'
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState<number | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [parentCelebrationId, setParentCelebrationId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<TaskViewMode>(() => {
    if (typeof window === 'undefined') {
      return 'list';
    }
    const savedView = window.localStorage.getItem(TASK_VIEW_MODE_STORAGE_KEY);
    return savedView === 'list' || savedView === 'goals' || savedView === 'calendar'
      ? savedView
      : 'list';
  });
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [taskBlockersLoading, setTaskBlockersLoading] = useState(false);
  const [taskBlockersError, setTaskBlockersError] = useState<string | null>(null);
  const [blockedByBlockers, setBlockedByBlockers] = useState<Blocker[]>([]);
  const [blockingBlockers, setBlockingBlockers] = useState<Blocker[]>([]);
  const [celebrationTasksForBlockers, setCelebrationTasksForBlockers] = useState<Task[]>([]);
  const [celebrationActivitiesForBlockers, setCelebrationActivitiesForBlockers] = useState<Activity[]>([]);
  const [celebrationGoalsForBlockers, setCelebrationGoalsForBlockers] = useState<GoalInstanceWithDetails[]>([]);
  const [celebrationEventsForBlockers, setCelebrationEventsForBlockers] = useState<Event[]>([]);
  const [activeBlockerEditor, setActiveBlockerEditor] = useState<BlockerEditorMode | null>(null);
  const [blockedByForm, setBlockedByForm] = useState<BlockedByInlineFormState>({
    entityType: 'task',
    entityId: '',
    eventStatus: 'active',
    mode: 'hard',
  });
  const [blockingForm, setBlockingForm] = useState<BlockingInlineFormState>({
    entityType: 'task',
    entityId: '',
    eventStatus: 'planned',
    mode: 'hard',
  });
  const [savingBlocker, setSavingBlocker] = useState(false);
  const [deletingBlockerId, setDeletingBlockerId] = useState<number | null>(null);
  const [pendingBlockedByBlockers, setPendingBlockedByBlockers] = useState<PendingCreateBlocker[]>([]);
  const [pendingBlockingBlockers, setPendingBlockingBlockers] = useState<PendingCreateBlocker[]>([]);
  const [blockedByByTaskId, setBlockedByByTaskId] = useState<Record<number, Blocker[]>>({});
  const [blockingByTaskId, setBlockingByTaskId] = useState<Record<number, Blocker[]>>({});
  const [taskFormError, setTaskFormError] = useState<string | null>(null);

  const entityId = parseInt(id || '0');
  const isCelebration = location.pathname.includes('/celebrations/');
  const celebrationContextId = isCelebration
    ? entityId
    : parentCelebrationId ?? tasks[0]?.celebration_id ?? null;

  const getApiErrorMessage = (err: unknown, fallbackMessage: string): string => {
    const maybeError = err as { response?: { data?: { message?: string } }; message?: string };
    return maybeError?.response?.data?.message || maybeError?.message || fallbackMessage;
  };

  useEffect(() => {
    if (entityId) {
      loadTasks();
      loadUsers();
      if (!isCelebration) {
        loadParentCelebration();
      }
    }
  }, [entityId, isCelebration]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TASK_VIEW_MODE_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  // Handle task deep-link highlighting from notifications and query params.
  useEffect(() => {
    const state = location.state as { highlightTaskId?: string; openTaskId?: string };
    const queryParams = new URLSearchParams(location.search);
    const taskIdFromQuery = queryParams.get('taskId');
    const highlightedId = state?.openTaskId || state?.highlightTaskId || taskIdFromQuery;

    if (!highlightedId) {
      return;
    }

    // Deep links should always open the full task list instead of grouped goal view.
    setViewMode('list');
    setHighlightedTaskId(highlightedId);
    setDeepLinkedTaskId(highlightedId);

    // Scroll to the task after a short delay to ensure it's rendered.
    setTimeout(() => {
      const element = document.getElementById(`task-${highlightedId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Clear highlight after 3 seconds.
    setTimeout(() => setHighlightedTaskId(null), 3000);

    // Clear transient link state/query to prevent re-highlighting on re-render.
    navigate(
      {
        pathname: location.pathname,
        search: '',
      },
      {
        replace: true,
        state: {},
      }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!deepLinkedTaskId || tasks.length === 0) {
      return;
    }

    const taskToOpen = tasks.find((task) => String(task.id) === deepLinkedTaskId);
    if (!taskToOpen) {
      return;
    }

    void handleEditTask(taskToOpen);
    setDeepLinkedTaskId(null);
  }, [deepLinkedTaskId, tasks]);

  const loadTasks = async (cacheBust = false, silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      let data;
      if (isCelebration) {
        data = await taskService.getCelebrationTasks(entityId, cacheBust);
      } else {
        data = await taskService.getEventTasks(entityId, cacheBust);
      }
      setTasks(data);
      if (!isCelebration && data.length > 0 && typeof data[0].celebration_id === 'number') {
        setParentCelebrationId(data[0].celebration_id);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load tasks');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getAllUsers();
      const mapping: Record<number, string> = {};
      usersData.forEach(user => {
        mapping[user.id] = user.full_name;
      });
      setUsernames(mapping);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadParentCelebration = async () => {
    try {
      const event = await eventService.getEvent(entityId);
      if (typeof event.celebration_id === 'number') {
        setParentCelebrationId(event.celebration_id);
      }
    } catch (err) {
      // Ignore; breadcrumbs will fallback to celebrations root until context is known.
    }
  };

  const handleDueDateChange = async (task: Task, newDate: string) => {
    if (!newDate) return;
    try {
      const updatedTask = {
        title: task.title,
        description: task.description,
        assigned_to: task.assigned_to,
        status: task.status,
        due_date: newDate
      };
      if (isCelebration) {
        await taskService.updateCelebrationTask(entityId, task.id, updatedTask);
      } else {
        await taskService.updateTask(entityId, task.id, updatedTask);
      }
      setTasks(tasks.map(t => t.id === task.id ? { ...t, due_date: newDate } : t));
      setEditingDueDate(null);
      setDraggedTask(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error?.response?.data?.message || 'Failed to update due date');
    }
  };

  const handleCalendarCellDrop = (e: React.DragEvent<HTMLDivElement>, dateKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedTask) {
      handleDueDateChange(draggedTask, fromDateKey(dateKey).toISOString());
    }
  };

  const handleTaskDragStart = (e: React.DragEvent, task: Task) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedTask(task);
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      if (isCelebration) {
        await taskService.deleteCelebrationTask(entityId, taskId);
      } else {
        await taskService.deleteTask(entityId, taskId);
      }
      await loadTasks(true, true);
    } catch (error) {
      alert('Failed to delete task.');
    }
  };

  const handleStatusChange = async (taskId: number, newStatus: TaskStatus) => {
    try {
      setError(null);
      if (isCelebration) {
        await taskService.updateCelebrationTask(entityId, taskId, { status: newStatus });
      } else {
        await taskService.updateTask(entityId, taskId, { status: newStatus });
      }
      await loadTasks(true, true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update status.'));
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const goalGroups = useMemo(() => {
    const grouped: Record<string, { label: string; tasks: Task[] }> = {};

    filteredTasks.forEach(task => {
      const goalName = task.goal_name?.trim();
      const hasGoalInstance = typeof task.goal_instance_id === 'number';
      const key = hasGoalInstance
        ? `goal-${task.goal_instance_id}`
        : goalName
          ? `goal-name-${goalName}`
          : 'no-goal';

      const label = hasGoalInstance
        ? goalName || `Goal #${task.goal_instance_id}`
        : goalName || 'No Goal';

      if (!grouped[key]) {
        grouped[key] = {
          label,
          tasks: [],
        };
      }

      grouped[key].tasks.push(task);
    });

    return Object.entries(grouped)
      .map(([key, value]) => ({ key, label: value.label, tasks: value.tasks }))
      .sort((a, b) => {
        if (a.key === 'no-goal') return 1;
        if (b.key === 'no-goal') return -1;
        return a.label.localeCompare(b.label);
      });
  }, [filteredTasks]);

  const tasksByDate = useMemo(() => {
    const grouped: Record<string, Task[]> = {};

    filteredTasks.forEach(task => {
      if (!task.due_date) {
        return;
      }

      const key = toDateKey(task.due_date);
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(task);
    });

    return grouped;
  }, [filteredTasks]);

  const noDueDateTasks = useMemo(
    () => filteredTasks.filter(task => !task.due_date),
    [filteredTasks]
  );

  const calendarCells = useMemo(() => {
    const firstDayOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
    const leadingEmptyCells = firstDayOfMonth.getDay();
    const cells: Array<Date | null> = [];

    for (let i = 0; i < leadingEmptyCells; i += 1) {
      cells.push(null);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
    }

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [calendarMonth]);

  useEffect(() => {
    if (viewMode !== 'calendar') {
      return;
    }

    const keysInMonth = Object.keys(tasksByDate)
      .filter(key => {
        const date = fromDateKey(key);
        return date.getFullYear() === calendarMonth.getFullYear() && date.getMonth() === calendarMonth.getMonth();
      })
      .sort();

    if (keysInMonth.length === 0) {
      if (selectedDateKey !== null) {
        setSelectedDateKey(null);
      }
      return;
    }

    if (!selectedDateKey || !keysInMonth.includes(selectedDateKey)) {
      setSelectedDateKey(keysInMonth[0]);
    }
  }, [calendarMonth, selectedDateKey, tasksByDate, viewMode]);

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    cancelled: tasks.filter(t => t.status === 'cancelled').length
  };

  const todayDateKey = toDateKey(new Date());
  const selectedDateTasks = selectedDateKey ? (tasksByDate[selectedDateKey] || []) : [];
  const calendarMonthLabel = calendarMonth.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const selectedDateLabel = selectedDateKey
    ? fromDateKey(selectedDateKey).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const entityOptionsByType = useMemo<Record<BlockerTargetEntityType, EntityOption[]>>(
    () => ({
      task: celebrationTasksForBlockers.map((task) => ({ id: task.id, label: task.title })),
      activity: celebrationActivitiesForBlockers.map((activity) => ({ id: activity.id, label: activity.title })),
      goal: celebrationGoalsForBlockers.map((goal) => ({
        id: goal.id,
        label: goal.template?.name || `Goal ${goal.id}`,
      })),
      event: celebrationEventsForBlockers.map((event) => ({ id: event.id, label: event.title })),
    }),
    [celebrationTasksForBlockers, celebrationActivitiesForBlockers, celebrationGoalsForBlockers, celebrationEventsForBlockers]
  );

  const getEntityOptionsForEditor = (formState: { entityType: BlockerTargetEntityType; entityId: string }): EntityOption[] => {
    const options = entityOptionsByType[formState.entityType] || [];
    if (!editingTask || formState.entityType !== 'task') {
      return options;
    }

    return options.filter((option) => option.id !== editingTask.id);
  };

  const getEntityLabel = (entityType: string, entityId: number | null): string => {
    if (entityId === null || entityId === undefined) {
      return entityType === 'time' ? 'Time blocker' : 'n/a';
    }

    if (entityType === 'task') {
      const taskOption = entityOptionsByType.task.find((option) => option.id === entityId);
      return taskOption ? `${taskOption.label}` : `Task #${entityId}`;
    }

    if (entityType === 'activity') {
      const activityOption = entityOptionsByType.activity.find((option) => option.id === entityId);
      return activityOption ? `${activityOption.label}` : `Activity #${entityId}`;
    }

    if (entityType === 'goal') {
      const goalOption = entityOptionsByType.goal.find((option) => option.id === entityId);
      return goalOption ? `${goalOption.label}` : `Goal #${entityId}`;
    }

    if (entityType === 'event') {
      const eventOption = entityOptionsByType.event.find((option) => option.id === entityId);
      return eventOption ? `${eventOption.label}` : `Event #${entityId}`;
    }

    return `#${entityId}`;
  };

  const getEntityStatus = (entityType: string, entityId: number | null): string | null => {
    if (entityId === null || entityId === undefined) {
      return null;
    }

    if (entityType === 'task') {
      const task = celebrationTasksForBlockers.find((item) => item.id === entityId);
      return task?.status || null;
    }

    if (entityType === 'activity') {
      const activity = celebrationActivitiesForBlockers.find((item) => item.id === entityId);
      return activity?.status || null;
    }

    if (entityType === 'goal') {
      const goal = celebrationGoalsForBlockers.find((item) => item.id === entityId);
      return goal?.status || null;
    }

    if (entityType === 'event') {
      const event = celebrationEventsForBlockers.find((item) => item.id === entityId);
      return event?.status || null;
    }

    return null;
  };

  const formatBlockerEntityDisplay = (entityType: string, entityId: number | null): string => {
    const label = getEntityLabel(entityType, entityId);
    const status = getEntityStatus(entityType, entityId);
    return status ? `${label} [${status}]` : label;
  };

  const fetchBlockingBlockers = async (taskId: number): Promise<Blocker[]> => {
    if (!celebrationContextId) {
      return [];
    }

    const celebrationBlockers = await blockerService.listByCelebration(celebrationContextId);
    return celebrationBlockers.filter((blocker) => blocker.blocker_type === 'task' && blocker.blocker_id === taskId);
  };

  const loadBlockerOptionsForCelebration = async () => {
    if (!celebrationContextId) {
      return;
    }

    try {
      if (celebrationTasksForBlockers.length === 0) {
        const tasksForScope = await taskService.getCelebrationTasks(celebrationContextId);
        setCelebrationTasksForBlockers(tasksForScope);
      }

      if (celebrationActivitiesForBlockers.length === 0) {
        const activitiesForScope = await activityService.getActivitiesByCelebration(celebrationContextId);
        setCelebrationActivitiesForBlockers(activitiesForScope);
      }

      if (celebrationGoalsForBlockers.length === 0) {
        const goalsForScope = await goalService.getInstancesByEntity('celebration', celebrationContextId);
        setCelebrationGoalsForBlockers(goalsForScope);
      }

      if (celebrationEventsForBlockers.length === 0) {
        const allEvents = await eventService.getEvents();
        setCelebrationEventsForBlockers(allEvents.filter((event) => event.celebration_id === celebrationContextId));
      }
    } catch (_err) {
      // Non-blocking: task creation should still work even if options fail to load.
    }
  };

  const loadTaskBlockerContext = async (includeOptions: boolean) => {
    if (!editingTask || !celebrationContextId) {
      return;
    }

    try {
      setTaskBlockersLoading(true);
      setTaskBlockersError(null);

      let tasksForScope = celebrationTasksForBlockers;
      let activitiesForScope = celebrationActivitiesForBlockers;
      let goalsForScope = celebrationGoalsForBlockers;
      let eventsForScope = celebrationEventsForBlockers;

      if (includeOptions || tasksForScope.length === 0) {
        tasksForScope = await taskService.getCelebrationTasks(celebrationContextId);
        setCelebrationTasksForBlockers(tasksForScope);
      }

      if (includeOptions || activitiesForScope.length === 0) {
        activitiesForScope = await activityService.getActivitiesByCelebration(celebrationContextId);
        setCelebrationActivitiesForBlockers(activitiesForScope);
      }

      if (includeOptions || goalsForScope.length === 0) {
        goalsForScope = await goalService.getInstancesByEntity('celebration', celebrationContextId);
        setCelebrationGoalsForBlockers(goalsForScope);
      }

      if (includeOptions || eventsForScope.length === 0) {
        const allEvents = await eventService.getEvents();
        eventsForScope = allEvents.filter((event) => event.celebration_id === celebrationContextId);
        setCelebrationEventsForBlockers(eventsForScope);
      }

      const blockedBy = await blockerService.listByBlockedEntity('task', editingTask.id);
      const blocking = await fetchBlockingBlockers(editingTask.id);

      setBlockedByBlockers(blockedBy);
      setBlockingBlockers(blocking);
    } catch (err: unknown) {
      setTaskBlockersError(err instanceof Error ? err.message : 'Failed to load blockers.');
      setBlockedByBlockers([]);
      setBlockingBlockers([]);
    } finally {
      setTaskBlockersLoading(false);
    }
  };

  useEffect(() => {
    if (!editingTask || !celebrationContextId) {
      setBlockedByBlockers([]);
      setBlockingBlockers([]);
      setTaskBlockersError(null);
      setActiveBlockerEditor(null);
      return;
    }

    void loadTaskBlockerContext(true);
  }, [editingTask?.id, celebrationContextId]);

  useEffect(() => {
    if (!showAddForm || !celebrationContextId) {
      return;
    }

    void loadBlockerOptionsForCelebration();
  }, [showAddForm, celebrationContextId]);

  useEffect(() => {
    if (!celebrationContextId || tasks.length === 0) {
      setBlockedByByTaskId({});
      setBlockingByTaskId({});
      return;
    }

    let isCancelled = false;

    const loadGridBlockers = async () => {
      try {
        const [tasksForScope, activitiesForScope, goalsForScope, eventsForScopeRaw] = await Promise.all([
          taskService.getCelebrationTasks(celebrationContextId),
          activityService.getActivitiesByCelebration(celebrationContextId),
          goalService.getInstancesByEntity('celebration', celebrationContextId),
          eventService.getEvents(),
        ]);

        const eventsForScope = eventsForScopeRaw.filter((event) => event.celebration_id === celebrationContextId);

        if (isCancelled) {
          return;
        }

        setCelebrationTasksForBlockers(tasksForScope);
        setCelebrationActivitiesForBlockers(activitiesForScope);
        setCelebrationGoalsForBlockers(goalsForScope);
        setCelebrationEventsForBlockers(eventsForScope);

        const celebrationBlockers = await blockerService.listByCelebration(celebrationContextId);

        const blockedByMap = tasks.reduce<Record<number, Blocker[]>>((acc, task) => {
          acc[task.id] = celebrationBlockers.filter(
            (blocker) => blocker.blocked_entity === 'task' && blocker.blocked_id === task.id
          );
          return acc;
        }, {});

        const blockingMap = tasks.reduce<Record<number, Blocker[]>>((acc, task) => {
          acc[task.id] = celebrationBlockers.filter((blocker) => blocker.blocker_type === 'task' && blocker.blocker_id === task.id);
          return acc;
        }, {});

        if (!isCancelled) {
          setBlockedByByTaskId(blockedByMap);
          setBlockingByTaskId(blockingMap);
        }
      } catch {
        if (!isCancelled) {
          setBlockedByByTaskId({});
          setBlockingByTaskId({});
        }
      }
    };

    void loadGridBlockers();

    return () => {
      isCancelled = true;
    };
  }, [celebrationContextId, tasks]);

  const createTaskBlocker = async (editorMode: BlockerEditorMode) => {
    if (!editingTask) {
      return;
    }

    const entityId = Number.parseInt((editorMode === 'blockedBy' ? blockedByForm.entityId : blockingForm.entityId), 10);
    if (!Number.isFinite(entityId) || entityId <= 0) {
      setTaskBlockersError('Please select a valid entity before saving blocker.');
      return;
    }

    try {
      setSavingBlocker(true);
      setTaskBlockersError(null);

      if (editorMode === 'blockedBy') {
        const formState = blockedByForm;
        await blockerService.createBlocker({
          blocked_entity: 'task',
          blocked_id: editingTask.id,
          blocker_type: formState.entityType,
          blocker_id: entityId,
          blocker_event_status: formState.entityType === 'event' ? formState.eventStatus : undefined,
          mode: formState.mode,
        });
      } else {
        const formState = blockingForm;
        await blockerService.createBlocker({
          blocked_entity: formState.entityType,
          blocked_id: entityId,
          blocked_event_status: formState.entityType === 'event' ? formState.eventStatus : undefined,
          blocker_type: 'task',
          blocker_id: editingTask.id,
          mode: formState.mode,
        });
      }

      setActiveBlockerEditor(null);
      setBlockedByForm({ entityType: 'task', entityId: '', eventStatus: 'active', mode: 'hard' });
      setBlockingForm({ entityType: 'task', entityId: '', eventStatus: 'planned', mode: 'hard' });
      await loadTaskBlockerContext(false);
    } catch (err: unknown) {
      setTaskBlockersError(err instanceof Error ? err.message : 'Failed to create blocker.');
    } finally {
      setSavingBlocker(false);
    }
  };

  const addPendingCreateBlocker = (editorMode: BlockerEditorMode) => {
    const selectedEntityId = Number.parseInt((editorMode === 'blockedBy' ? blockedByForm.entityId : blockingForm.entityId), 10);
    if (!Number.isFinite(selectedEntityId) || selectedEntityId <= 0) {
      setTaskBlockersError('Please select a valid entity before adding blocker.');
      return;
    }

    setTaskBlockersError(null);

    if (editorMode === 'blockedBy') {
      setPendingBlockedByBlockers((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          entityType: blockedByForm.entityType,
          entityId: selectedEntityId,
          eventStatus: blockedByForm.entityType === 'event' ? blockedByForm.eventStatus : undefined,
          mode: blockedByForm.mode,
        },
      ]);
    } else {
      setPendingBlockingBlockers((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          entityType: blockingForm.entityType,
          entityId: selectedEntityId,
          eventStatus: blockingForm.entityType === 'event' ? blockingForm.eventStatus : undefined,
          mode: blockingForm.mode,
        },
      ]);
    }

    setActiveBlockerEditor(null);
    setBlockedByForm({ entityType: 'task', entityId: '', eventStatus: 'active', mode: 'hard' });
    setBlockingForm({ entityType: 'task', entityId: '', eventStatus: 'planned', mode: 'hard' });
  };

  const removePendingCreateBlocker = (editorMode: BlockerEditorMode, pendingId: string) => {
    if (editorMode === 'blockedBy') {
      setPendingBlockedByBlockers((prev) => prev.filter((item) => item.id !== pendingId));
      return;
    }

    setPendingBlockingBlockers((prev) => prev.filter((item) => item.id !== pendingId));
  };

  const deleteTaskBlocker = async (blockerId: number) => {
    try {
      setDeletingBlockerId(blockerId);
      setTaskBlockersError(null);
      await blockerService.deleteBlocker(blockerId);
      await loadTaskBlockerContext(false);
    } catch (err: unknown) {
      setTaskBlockersError(err instanceof Error ? err.message : 'Failed to delete blocker.');
    } finally {
      setDeletingBlockerId(null);
    }
  };

  const handleEditTask = async (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to,
      due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
      status: task.status
    });
    
    if (task.assigned_to) {
      try {
        // Task `assigned_to` is always a user id.
        const user = await userService.getUserById(task.assigned_to);
        const userAsContact: UserContact = {
          id: user.id,
          owner_user_id: user.user_id,
          user_id: user.user_id,
          full_name: user.full_name,
          email: user.email || '',
          phone: user.phone || '',
          relationship: 'user',
          is_primary: false,
          status: 'active',
          dietary_preferences: '',
          special_requirements: '',
          metadata: {},
          source: 'user_contact',
          created_at: new Date(),
          updated_at: new Date()
        };
        setSelectedUser(userAsContact);
      } catch (error) {
        console.error('Failed to load assigned user:', error);
        setSelectedUser(null);
      }
    } else {
      setSelectedUser(null);
    }
    setShowAddForm(true);
    setTaskFormError(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assigned_to: undefined,
      due_date: '',
      status: 'pending'
    });
    setFormErrors({});
    setEditingTask(null);
    setShowAddForm(false);
    setSelectedUser(null);
    setPendingBlockedByBlockers([]);
    setPendingBlockingBlockers([]);
    setBlockedByForm({ entityType: 'task', entityId: '', eventStatus: 'active', mode: 'hard' });
    setBlockingForm({ entityType: 'task', entityId: '', eventStatus: 'planned', mode: 'hard' });
    setTaskBlockersError(null);
    setActiveBlockerEditor(null);
    setTaskFormError(null);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.title.trim()) errors.title = 'Title required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    setTaskFormError(null);
    try {
      const resolvedUserId = selectedUser ? selectedUser.id : undefined;
      const taskData = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
        assigned_to: resolvedUserId
      };

      if (editingTask) {
        // Update task
        if (isCelebration) {
          await taskService.updateCelebrationTask(entityId, editingTask.id, taskData);
        } else {
          await taskService.updateTask(entityId, editingTask.id, taskData);
        }
      } else {
        // Create task
        let createdTask: Task;
        if (isCelebration) {
          createdTask = await taskService.createCelebrationTask(entityId, taskData);
        } else {
          createdTask = await taskService.createTask(entityId, taskData);
        }

        const pendingCreates = [
          ...pendingBlockedByBlockers.map((pending) => blockerService.createBlocker({
            blocked_entity: 'task',
            blocked_id: createdTask.id,
            blocker_type: pending.entityType,
            blocker_id: pending.entityId,
            blocker_event_status: pending.entityType === 'event' ? pending.eventStatus : undefined,
            mode: pending.mode,
          })),
          ...pendingBlockingBlockers.map((pending) => blockerService.createBlocker({
            blocked_entity: pending.entityType,
            blocked_id: pending.entityId,
            blocked_event_status: pending.entityType === 'event' ? pending.eventStatus : undefined,
            blocker_type: 'task',
            blocker_id: createdTask.id,
            mode: pending.mode,
          })),
        ];

        if (pendingCreates.length > 0) {
          try {
            await Promise.all(pendingCreates);
          } catch (_err) {
            setError('Task created, but one or more blockers failed to save. Edit the task to retry blocker setup.');
            setTimeout(() => setError(null), 7000);
          }
        }
      }
      await loadTasks(true);
      resetForm();
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Failed to save task.');
      setTaskFormError(message);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const goToPreviousMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="default"
        title="Loading tasks"
        description="Please wait while we load task details."
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: t('common:nav.home'), to: '/home' },
          { label: t('common:nav.celebrations'), to: '/celebrations' },
          { label: 'Celebration', to: celebrationContextId ? `/celebrations/${celebrationContextId}` : undefined },
          ...(!isCelebration ? [{ label: 'Event', to: `/events/${entityId}` }] : []),
          { label: t('common:nav.tasks') },
        ]}
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <CheckSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">{t('common:nav.tasks')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Plan and track work for this {isCelebration ? 'celebration' : 'event'}.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(isCelebration ? (celebrationContextId ? `/celebrations/${celebrationContextId}` : '/celebrations') : `/events/${entityId}`)}
              >
                Back
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => celebrationContextId && navigate(`/celebrations/${celebrationContextId}/blockers`)}
                disabled={!celebrationContextId}
              >
                <Link2 className="h-3 w-3 mr-1" />
                Blockers
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowTemplatePicker(true)}>
                <Sparkles className="h-3 w-3 mr-1" />
                Template
              </Button>
              <Button size="sm" onClick={() => setShowAddForm(true)}>
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
          </div>
        }
        columns={5}
        items={[
          {
            key: 'total',
            icon: CheckSquare,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            label: 'Total',
            value: stats.total,
          },
          {
            key: 'pending',
            icon: Clock3,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            label: t('common:status.pending'),
            value: stats.pending,
          },
          {
            key: 'in-progress',
            icon: Sparkles,
            iconClassName: 'text-cyan-600',
            hoverClassName: 'hover:bg-cyan-50',
            label: 'In Progress',
            value: stats.inProgress,
          },
          {
            key: 'completed',
            icon: CheckCircle2,
            iconClassName: 'text-emerald-600',
            hoverClassName: 'hover:bg-emerald-50',
            label: t('common:status.completed'),
            value: stats.completed,
          },
          {
            key: 'cancelled',
            icon: X,
            iconClassName: 'text-red-600',
            hoverClassName: 'hover:bg-red-50',
            label: t('common:status.cancelled'),
            value: stats.cancelled,
          },
          {
            key: 'view-mode',
            icon: Target,
            iconClassName: 'text-violet-600',
            hoverClassName: 'hover:bg-violet-50',
            label: 'View',
            value: viewMode.charAt(0).toUpperCase() + viewMode.slice(1),
          },
        ]}
      />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Compact Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
            compact
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'all')}
          aria-label="Filter tasks by status"
          title="Filter tasks by status"
          className="h-7 px-2 text-sm rounded-md border border-input bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <option value="all">All</option>
          <option value="pending">⏳ Pending</option>
          <option value="in_progress">🔄 In Progress</option>
          <option value="completed">✓ Completed</option>
          <option value="cancelled">🚫 Cancelled</option>
        </select>
      </div>

      {/* Display Mode */}
      <div className="rounded-lg border border-input bg-muted/20 px-3 py-2">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-medium text-muted-foreground">Display mode</span>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="task-display-mode"
              value="list"
              checked={viewMode === 'list'}
              onChange={() => setViewMode('list')}
              className="h-3.5 w-3.5 accent-primary"
            />
            <span>List</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="task-display-mode"
              value="goals"
              checked={viewMode === 'goals'}
              onChange={() => setViewMode('goals')}
              className="h-3.5 w-3.5 accent-primary"
            />
            <span>Goals</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="task-display-mode"
              value="calendar"
              checked={viewMode === 'calendar'}
              onChange={() => setViewMode('calendar')}
              className="h-3.5 w-3.5 accent-primary"
            />
            <span>Calendar</span>
          </label>
        </div>
      </div>

      {/* Task Views */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between">
          <span className="text-sm font-medium">{filteredTasks.length} tasks</span>
          <span className="text-xs text-muted-foreground capitalize">{viewMode} view</span>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="text-center py-8 px-4">
            <div className="text-2xl mb-2">📋</div>
            <p className="text-sm font-medium">No tasks found</p>
            <p className="text-xs text-muted-foreground mt-1">
              {isCelebration 
                ? 'Tasks are created when you add goals to your celebration. Go back and add a goal to get started!' 
                : tasks.length === 0 
                  ? 'Create your first task' 
                  : 'Try adjusting filters'}
            </p>
          </div>
        ) : (
          <>
            {viewMode === 'list' && (
              <TaskGrid
                tasks={filteredTasks}
                showGoalColumn={true}
                highlightedTaskId={highlightedTaskId}
                blockedByByTaskId={blockedByByTaskId}
                blockingByTaskId={blockingByTaskId}
                usernames={usernames}
                editingDueDate={editingDueDate}
                onEditingDueDateChange={setEditingDueDate}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onStatusChange={handleStatusChange}
                onDueDateChange={handleDueDateChange}
                formatBlockerDisplay={formatBlockerEntityDisplay}
                withContainer={false}
              />
            )}

            {viewMode === 'goals' && (
              <div className="divide-y">
                {goalGroups.map(group => (
                  <div key={group.key}>
                    <div className={`px-4 py-2.5 border-b flex items-center justify-between ${group.key === 'no-goal' ? 'bg-slate-50' : 'bg-gradient-to-r from-violet-50 via-blue-50 to-white'}`}>
                      <div className="min-w-0 flex items-center gap-2">
                        <div className="h-6 w-6 rounded-md bg-white/90 border border-violet-200 flex items-center justify-center">
                          <Target className="h-3.5 w-3.5 text-violet-600" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold truncate block">{group.label}</span>
                          <span className="text-[11px] text-muted-foreground">{group.tasks.length} tasks</span>
                        </div>
                      </div>
                      <Badge variant="secondary" size="sm" className="gap-1">
                        <span>{group.tasks.filter(task => task.status === 'completed').length}</span>
                        <span className="text-muted-foreground">/ {group.tasks.length}</span>
                      </Badge>
                    </div>
                    <TaskGrid
                      tasks={group.tasks}
                      showGoalColumn={false}
                      highlightedTaskId={highlightedTaskId}
                      blockedByByTaskId={blockedByByTaskId}
                      blockingByTaskId={blockingByTaskId}
                      usernames={usernames}
                      editingDueDate={editingDueDate}
                      onEditingDueDateChange={setEditingDueDate}
                      onEditTask={handleEditTask}
                      onDeleteTask={handleDeleteTask}
                      onStatusChange={handleStatusChange}
                      onDueDateChange={handleDueDateChange}
                      formatBlockerDisplay={formatBlockerEntityDisplay}
                      withContainer={false}
                    />
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'calendar' && (
              <>
                <div className="px-4 py-2 border-b bg-muted/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon-sm" onClick={goToPreviousMonth} aria-label="Previous month">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[140px] text-center">{calendarMonthLabel}</span>
                    <Button variant="ghost" size="icon-sm" onClick={goToNextMonth} aria-label="Next month">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="xs" onClick={goToCurrentMonth}>Today</Button>
                  </div>
                  <span className="text-xs text-muted-foreground">{Object.keys(tasksByDate).length} due-date buckets</span>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-5 gap-0 border-b">
                  {/* Calendar */}
                  <div className="xl:col-span-4">
                    <div className="grid grid-cols-7 border-b bg-muted/20">
                      {CALENDAR_WEEKDAY_LABELS.map(day => (
                        <div key={day} className="px-2 py-1.5 text-xs font-medium text-muted-foreground text-center border-r last:border-r-0">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7">
                      {calendarCells.map((cell, index) => {
                        if (!cell) {
                          return (
                            <div
                              key={`calendar-empty-${index}`}
                              className="h-24 border-r border-b last:border-r-0 bg-muted/10"
                            />
                          );
                        }

                        const dateKey = toDateKey(cell);
                        const dayTasks = tasksByDate[dateKey] || [];
                        const isToday = dateKey === todayDateKey;
                        const isSelected = dateKey === selectedDateKey;
                        const isDragOverCell = draggedTask?.due_date ? toDateKey(draggedTask.due_date) === dateKey : false;

                        return (
                          <div
                            key={dateKey}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={(e) => handleCalendarCellDrop(e, dateKey)}
                            className={`h-24 border-r border-b last:border-r-0 p-1.5 text-left transition-colors cursor-pointer ${
                              isSelected ? 'bg-primary/10' : isDragOverCell ? 'bg-green-100' : 'hover:bg-muted/20'
                            }`}
                            onClick={() => setSelectedDateKey(dateKey)}
                            title={`Select ${cell.toDateString()}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-foreground'}`}>
                                {cell.getDate()}
                              </span>
                              {dayTasks.length > 0 && (
                                <Badge variant="secondary" size="sm" className="h-5 px-1.5 text-[10px]">
                                  {dayTasks.length}
                                </Badge>
                              )}
                            </div>
                            {dayTasks.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {dayTasks.slice(0, 2).map(task => (
                                  <p key={task.id} className="text-[11px] truncate text-foreground/80">
                                    {task.title}
                                  </p>
                                ))}
                                {dayTasks.length > 2 && (
                                  <p className="text-[11px] text-muted-foreground">+{dayTasks.length - 2} more</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right panel: No due date tasks */}
                  {noDueDateTasks.length > 0 && (
                    <div className="xl:col-span-1 border-l bg-slate-50/50 flex flex-col">
                      <div className="px-3 py-2 border-b bg-muted/30 sticky top-0 text-sm font-semibold text-muted-foreground flex items-center justify-between">
                        <span>No Date ({noDueDateTasks.length})</span>
                      </div>
                      <div className="flex-1 overflow-y-auto space-y-1 p-2">
                        {noDueDateTasks.map(task => (
                          <div
                            key={task.id}
                            draggable
                            onDragStart={(e) => handleTaskDragStart(e, task)}
                            onDragEnd={handleTaskDragEnd}
                            className={`p-2 rounded-md border text-xs cursor-move select-none transition-all hover:shadow-sm ${
                              draggedTask?.id === task.id
                                ? 'bg-orange-100 border-orange-300 opacity-50'
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                            title={task.description ? `${task.title}\n${task.description}` : task.title}
                          >
                            <p className="font-medium truncate text-foreground">{task.title}</p>
                            {task.description && (
                              <p className="text-muted-foreground truncate text-[10px] mt-0.5">{task.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t">
                  <div className="px-4 py-2 bg-muted/10">
                    <span className="text-sm font-medium">
                      {selectedDateLabel ? `Tasks for ${selectedDateLabel}` : 'Select a day to view tasks'}
                    </span>
                  </div>

                  {!selectedDateLabel ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground">Pick a calendar day to inspect due tasks.</div>
                  ) : selectedDateTasks.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground">No tasks due on the selected day.</div>
                  ) : (
                    <TaskGrid
                      tasks={selectedDateTasks}
                      showGoalColumn={true}
                      highlightedTaskId={highlightedTaskId}
                      blockedByByTaskId={blockedByByTaskId}
                      blockingByTaskId={blockingByTaskId}
                      usernames={usernames}
                      editingDueDate={editingDueDate}
                      onEditingDueDateChange={setEditingDueDate}
                      onEditTask={handleEditTask}
                      onDeleteTask={handleDeleteTask}
                      onStatusChange={handleStatusChange}
                      onDueDateChange={handleDueDateChange}
                      formatBlockerDisplay={formatBlockerEntityDisplay}
                      withContainer={false}
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Task Template Picker */}
      <TaskTemplatePicker
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        celebrationId={isCelebration ? entityId : undefined}
        eventId={!isCelebration ? entityId : undefined}
        onApplied={() => loadTasks(true)}
      />

      {/* Add/Edit Task Modal - Compact */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl shadow-xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold text-sm">{editingTask ? 'Edit Task' : 'Add Task'}</h3>
              <Button variant="ghost" size="icon-sm" onClick={resetForm}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto max-h-[calc(90vh-56px)]">
              {taskFormError && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
                  {taskFormError}
                </div>
              )}

              <div>
                <Label htmlFor="title" className="text-xs">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Task title"
                  className={formErrors.title ? 'border-red-500' : ''}
                  compact
                />
                {formErrors.title && <p className="text-xs text-red-600 mt-0.5">{formErrors.title}</p>}
              </div>

              <div>
                <Label htmlFor="description" className="text-xs">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Details..."
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="due_date" className="text-xs">Due Date</Label>
                  <Input
                    id="due_date"
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    compact
                  />
                </div>
                <div>
                  <Label htmlFor="status" className="text-xs">Status</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                    aria-label="Task status"
                    title="Task status"
                    className="w-full h-7 px-2 text-sm rounded-md border border-input bg-background"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">🚫 Cancelled</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Assign To</Label>
                <UserSearchInput
                  selectedUser={selectedUser}
                  onUserSelect={setSelectedUser}
                  placeholder="Search person..."
                  className="mt-1"
                />
              </div>

              {celebrationContextId && (
                <div className="rounded-md border border-border/70 bg-slate-50 p-3 space-y-3">
                  <div>
                    <p className="text-sm font-semibold">Dependency Blockers</p>
                    <p className="text-xs text-muted-foreground">Manage what blocks this task and what this task blocks.</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Task dependencies use hard blockers. Supported task relationships: Task/Goal/Event can block this task, and this task can block Task/Goal/Event.</p>
                  </div>

                  {taskBlockersError && (
                    <p className="text-xs text-red-600">{taskBlockersError}</p>
                  )}

                  {taskBlockersLoading ? (
                    <p className="text-xs text-muted-foreground">Loading blockers...</p>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-md border bg-white p-2.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold">Blocked by</p>
                          <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(activeBlockerEditor === 'blockedBy' ? null : 'blockedBy')}>
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>

                        {(editingTask ? blockedByBlockers.length : pendingBlockedByBlockers.length) === 0 ? (
                          <p className="text-xs text-muted-foreground">No blockers are currently blocking this task.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {editingTask
                              ? blockedByBlockers.map((blocker) => (
                                  <div key={blocker.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="truncate">{getEntityLabel(blocker.blocker_type, blocker.blocker_id)}</p>
                                      <p className="text-muted-foreground">
                                        mode: {blocker.mode}
                                        {blocker.blocker_type === 'event' && blocker.blocker_event_status
                                          ? ` · requires event >= ${blocker.blocker_event_status}`
                                          : ''}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="xs"
                                      className="text-destructive"
                                      disabled={deletingBlockerId === blocker.id}
                                      onClick={() => void deleteTaskBlocker(blocker.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                ))
                              : pendingBlockedByBlockers.map((pending) => (
                                  <div key={pending.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="truncate">{getEntityLabel(pending.entityType, pending.entityId)}</p>
                                      <p className="text-muted-foreground">
                                        mode: {pending.mode}
                                        {pending.entityType === 'event' && pending.eventStatus
                                          ? ` · requires event >= ${pending.eventStatus}`
                                          : ''}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="xs"
                                      className="text-destructive"
                                      onClick={() => removePendingCreateBlocker('blockedBy', pending.id)}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ))}
                          </div>
                        )}

                        {activeBlockerEditor === 'blockedBy' && (
                          <div className="rounded-md border border-dashed p-2 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[11px]">Entity</Label>
                                <select
                                  value={blockedByForm.entityType}
                                  onChange={(e) => setBlockedByForm((prev) => ({
                                    ...prev,
                                    entityType: e.target.value as TaskBlockedByEntityType,
                                    entityId: '',
                                    eventStatus: 'active',
                                  }))}
                                  title="Blocked by entity"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                >
                                  <option value="task">Task</option>
                                  <option value="goal">Goal</option>
                                  <option value="event">Event</option>
                                </select>
                              </div>
                              <div>
                                <Label className="text-[11px]">Mode</Label>
                                <select
                                  value={blockedByForm.mode}
                                  onChange={(e) => setBlockedByForm((prev) => ({ ...prev, mode: e.target.value as BlockerMode }))}
                                  title="Blocker mode"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                  disabled
                                >
                                  <option value="hard">Hard</option>
                                </select>
                              </div>
                            </div>

                            {blockedByForm.entityType === 'event' && (
                              <div>
                                <Label className="text-[11px]">Required Event Status</Label>
                                <select
                                  value={blockedByForm.eventStatus}
                                  onChange={(e) => setBlockedByForm((prev) => ({ ...prev, eventStatus: e.target.value }))}
                                  title="Required blocker event status"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                >
                                  {EVENT_STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <div>
                              <Label className="text-[11px]">Name / Title</Label>
                              <select
                                value={blockedByForm.entityId}
                                onChange={(e) => setBlockedByForm((prev) => ({ ...prev, entityId: e.target.value }))}
                                title="Blocked by target"
                                className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                              >
                                <option value="">Select {blockedByForm.entityType}</option>
                                {getEntityOptionsForEditor(blockedByForm).map((option) => (
                                  <option key={option.id} value={option.id}>{option.label}</option>
                                ))}
                              </select>
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(null)}>
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="xs"
                                disabled={savingBlocker || !blockedByForm.entityId}
                                onClick={() => void (editingTask ? createTaskBlocker('blockedBy') : addPendingCreateBlocker('blockedBy'))}
                              >
                                {savingBlocker ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="rounded-md border bg-white p-2.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold">Blocking</p>
                          <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(activeBlockerEditor === 'blocking' ? null : 'blocking')}>
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>

                        {(editingTask ? blockingBlockers.length : pendingBlockingBlockers.length) === 0 ? (
                          <p className="text-xs text-muted-foreground">This task is not currently blocking other entities.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {editingTask
                              ? blockingBlockers.map((blocker) => (
                                  <div key={blocker.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="truncate">{getEntityLabel(blocker.blocked_entity, blocker.blocked_id)}</p>
                                      <p className="text-muted-foreground">
                                        mode: {blocker.mode}
                                        {blocker.blocked_entity === 'event' && blocker.blocked_event_status
                                          ? ` · blocks event status >= ${blocker.blocked_event_status}`
                                          : ''}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="xs"
                                      className="text-destructive"
                                      disabled={deletingBlockerId === blocker.id}
                                      onClick={() => void deleteTaskBlocker(blocker.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                ))
                              : pendingBlockingBlockers.map((pending) => (
                                  <div key={pending.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="truncate">{getEntityLabel(pending.entityType, pending.entityId)}</p>
                                      <p className="text-muted-foreground">
                                        mode: {pending.mode}
                                        {pending.entityType === 'event' && pending.eventStatus
                                          ? ` · blocks event status >= ${pending.eventStatus}`
                                          : ''}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="xs"
                                      className="text-destructive"
                                      onClick={() => removePendingCreateBlocker('blocking', pending.id)}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ))}
                          </div>
                        )}

                        {activeBlockerEditor === 'blocking' && (
                          <div className="rounded-md border border-dashed p-2 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[11px]">Entity</Label>
                                <select
                                  value={blockingForm.entityType}
                                  onChange={(e) => setBlockingForm((prev) => ({
                                    ...prev,
                                    entityType: e.target.value as TaskBlockingEntityType,
                                    entityId: '',
                                    eventStatus: 'planned',
                                  }))}
                                  title="Blocking entity"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                >
                                  <option value="task">Task</option>
                                  <option value="goal">Goal</option>
                                  <option value="event">Event</option>
                                </select>
                              </div>
                              <div>
                                <Label className="text-[11px]">Mode</Label>
                                <select
                                  value={blockingForm.mode}
                                  onChange={(e) => setBlockingForm((prev) => ({ ...prev, mode: e.target.value as BlockerMode }))}
                                  title="Blocking mode"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                  disabled
                                >
                                  <option value="hard">Hard</option>
                                </select>
                              </div>
                            </div>

                            {blockingForm.entityType === 'event' && (
                              <div>
                                <Label className="text-[11px]">Blocked Event Status</Label>
                                <select
                                  value={blockingForm.eventStatus}
                                  onChange={(e) => setBlockingForm((prev) => ({ ...prev, eventStatus: e.target.value }))}
                                  title="Blocked event target status"
                                  className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                                >
                                  {EVENT_STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <div>
                              <Label className="text-[11px]">Name / Title</Label>
                              <select
                                value={blockingForm.entityId}
                                onChange={(e) => setBlockingForm((prev) => ({ ...prev, entityId: e.target.value }))}
                                title="Blocking target"
                                className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                              >
                                <option value="">Select {blockingForm.entityType}</option>
                                {getEntityOptionsForEditor(blockingForm).map((option) => (
                                  <option key={option.id} value={option.id}>{option.label}</option>
                                ))}
                              </select>
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(null)}>
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="xs"
                                disabled={savingBlocker || !blockingForm.entityId}
                                onClick={() => void (editingTask ? createTaskBlocker('blocking') : addPendingCreateBlocker('blocking'))}
                              >
                                {savingBlocker ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button type="submit" size="sm" className="flex-1" disabled={submitting}>
                  {submitting ? 'Saving...' : (editingTask ? 'Update' : 'Add')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={resetForm} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
